import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import LandingPage from "./pages/LandingPage";

// Admin
import AdminDashboard from "./pages/admin/AdminDashboard";
import RestaurantsList from "./pages/admin/RestaurantsList";
import RestaurantDetail from "./pages/admin/RestaurantDetail";

// Manager
import ManagerDashboard from "./pages/manager/ManagerDashboard";
import MenuManager from "./pages/manager/MenuManager";
import ManagerSettings from "./pages/manager/ManagerSettings";
import ManagerReports from "./pages/manager/ManagerReports";
import ManagerOrders from "./pages/manager/ManagerOrders";
import TablesManager from "./pages/manager/TablesManager";
import StaffManagement from "./pages/manager/StaffManagement";
import Analytics from "./pages/manager/Analytics";

// Kitchen & Cashier
import KitchenDashboard from "./pages/kitchen/KitchenDashboard";
import FoodKitchen from "./pages/kitchen/FoodKitchen";
import DrinkKitchen from "./pages/kitchen/DrinkKitchen";
import CashierDashboard from "./pages/cashier/CashierDashboard";
import CashierOrders from "./pages/cashier/CashierOrders";

// Customer
import CustomerOrder from "./pages/customer/CustomerOrder";
import OrderTracking from "./pages/customer/OrderTracking";

// Waiter
import WaiterDashboard from "./pages/waiter/WaiterDashboard";

import "./App.css";

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/demo" element={<Navigate to="/r/java-cafe" replace />} />
            
            {/* Customer Order Flow */}
            <Route path="/r/:slug" element={<CustomerOrder />} />
            <Route path="/track/:orderId" element={<OrderTracking />} />

          {/* Super Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/restaurants"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <RestaurantsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/restaurants/:restaurantId"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <RestaurantDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <RestaurantsList />
              </ProtectedRoute>
            }
          />

          {/* Manager Routes */}
          <Route
            path="/manager"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <ManagerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/menu"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <MenuManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/orders"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <ManagerOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/reports"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <ManagerReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/settings"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <ManagerSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/tables"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <TablesManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/staff"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <StaffManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/analytics"
            element={
              <ProtectedRoute allowedRoles={["owner", "manager"]}>
                <Analytics />
              </ProtectedRoute>
            }
          />


          {/* Kitchen Routes */}
          <Route
            path="/kitchen"
            element={
              <ProtectedRoute allowedRoles={["kitchen", "owner", "manager"]}>
                <KitchenDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kitchen/food"
            element={
              <ProtectedRoute allowedRoles={["kitchen", "owner", "manager"]}>
                <FoodKitchen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kitchen/drinks"
            element={
              <ProtectedRoute allowedRoles={["kitchen", "owner", "manager"]}>
                <DrinkKitchen />
              </ProtectedRoute>
            }
          />

          {/* Cashier Routes */}
          <Route
            path="/cashier"
            element={
              <ProtectedRoute allowedRoles={["cashier", "owner", "manager"]}>
                <CashierDashboard />
              </ProtectedRoute>
            }
          />          <Route
            path="/cashier/orders"
            element={
              <ProtectedRoute allowedRoles={["cashier", "owner", "manager"]}>
                <CashierOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashier/reports"
            element={
              <ProtectedRoute allowedRoles={["cashier", "owner", "manager"]}>
                <ManagerReports />
              </ProtectedRoute>
            }
          />

          {/* Waiter Routes — mobile-first, no payment access */}
          <Route
            path="/waiter"
            element={
              <ProtectedRoute allowedRoles={["waiter", "owner", "manager"]}>
                <WaiterDashboard />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
